import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import PublicHeader from '../components/layout/PublicHeader';
import { FREE_TOOLS, CATEGORY_LABELS, type ToolCategory } from '../data/freeTools';
import {
  getToolSEO,
  getToolCanonicalUrl,
  getToolFAQSchema,
  getToolSoftwareApplicationSchema,
  getToolHowToSchema,
} from '../data/toolSEOData';
import {
  getProgrammaticSEO,
  isProgrammaticTool,
  isValidProgrammaticSlug,
  getProgrammaticFAQSchema,
  getProgrammaticSoftwareAppSchema,
  type ProgrammaticToolId,
} from '../data/programmaticToolSEO';
import { useAppSelector } from '../store/hooks';
import QRCodeTool from '../components/free-tools/QRCodeTool';
import WordCounterTool from '../components/free-tools/WordCounterTool';
import PasswordGeneratorTool from '../components/free-tools/PasswordGeneratorTool';
import UnitConverterTool from '../components/free-tools/UnitConverterTool';
import CurrencyConverterTool from '../components/free-tools/CurrencyConverterTool';

const COLOR_CLASSES: Record<string, { bg: string; text: string }> = {
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
};

export interface FreeToolSlugProps {
  slug?: string;
}

const TOOL_COMPONENTS: Record<string, React.ComponentType<FreeToolSlugProps>> = {
  'qr-code': QRCodeTool,
  'word-counter': WordCounterTool,
  'password-generator': PasswordGeneratorTool,
  'unit-converter': UnitConverterTool,
  'currency-converter': CurrencyConverterTool,
};

const CATEGORY_ORDER: ToolCategory[] = ['generator', 'calculator', 'general'];

const FreeToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toolId, slug } = useParams<{ toolId?: string; slug?: string }>();
  const { isAuthenticated } = useAppSelector((state) => state.user);

  const visibleTools = FREE_TOOLS.filter((t) => !t.requiresLogin || isAuthenticated);
  const selectedTool = toolId ? visibleTools.find((t) => t.id === toolId) ?? FREE_TOOLS.find((t) => t.id === toolId) : null;
  const ToolComponent = toolId ? TOOL_COMPONENTS[toolId] : null;

  const baseUrl = 'https://focuzon.com';
  const seo = selectedTool ? getToolSEO(selectedTool.id) : null;
  const canonicalUrl = selectedTool ? getToolCanonicalUrl(selectedTool.id, slug) : null;

  const progSEO =
    toolId && slug && isProgrammaticTool(toolId) && isValidProgrammaticSlug(toolId, slug)
      ? getProgrammaticSEO(toolId, slug)
      : null;

  const faqSchemaJson = progSEO?.faqs?.length
    ? getProgrammaticFAQSchema(progSEO.faqs)
    : seo?.faqs?.length
      ? getToolFAQSchema(seo.faqs)
      : null;
  const softwareAppSchema = progSEO && toolId
    ? getProgrammaticSoftwareAppSchema(toolId as ProgrammaticToolId, progSEO.h1)
    : selectedTool && seo
      ? getToolSoftwareApplicationSchema(selectedTool.id, selectedTool.name, selectedTool.description)
      : null;
  const howToSchemaJson =
    !progSEO && seo?.howToSchemaSteps?.length && seo.h1
      ? getToolHowToSchema(`How to use ${seo.h1}`, seo.howToSchemaSteps)
      : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [toolId, slug]);

  return (
    <>
      <Helmet>
        {selectedTool ? (
          <>
            <title>{progSEO?.title ?? seo?.title ?? `${selectedTool.name} - Free Tool | Focuzon`}</title>
            <meta
              name="description"
              content={progSEO?.metaDescription ?? seo?.metaDescription ?? `${selectedTool.description} - Use this free tool without login.`}
            />
            <link rel="canonical" href={canonicalUrl ?? `${baseUrl}/tools/${selectedTool.id}`} />
            <meta property="og:title" content={progSEO?.ogTitle ?? seo?.ogTitle ?? `${selectedTool.name} | Focuzon`} />
            <meta property="og:description" content={progSEO?.ogDescription ?? seo?.ogDescription ?? selectedTool.description} />
            <meta property="og:url" content={canonicalUrl ?? `${baseUrl}/tools/${selectedTool.id}`} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={`${baseUrl}/og-image.jpg`} />
            <meta name="twitter:title" content={progSEO?.ogTitle ?? seo?.ogTitle ?? `${selectedTool.name} | Focuzon`} />
            <meta name="twitter:description" content={progSEO?.ogDescription ?? seo?.ogDescription ?? selectedTool.description} />
            {faqSchemaJson && <script type="application/ld+json">{JSON.stringify(faqSchemaJson)}</script>}
            {softwareAppSchema && <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>}
            {howToSchemaJson && <script type="application/ld+json">{JSON.stringify(howToSchemaJson)}</script>}
          </>
        ) : (
          <>
            <title>Free Productivity Tools | Password Generator, Currency Converter & More | Focuzon</title>
            <meta
              name="description"
              content="Free tools: password generator, currency converter, word counter, unit converter, QR code generator. No login required."
            />
            <link rel="canonical" href={`${baseUrl}/tools`} />
            <meta property="og:title" content="Free Productivity Tools | Focuzon" />
            <meta property="og:description" content="Free tools: password generator, currency converter, word counter, unit converter. Start your focus session with Focuzon." />
            <meta property="og:url" content={`${baseUrl}/tools`} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={`${baseUrl}/og-image.jpg`} />
            <meta name="twitter:title" content="Free Productivity Tools | Focuzon" />
            <meta name="twitter:description" content="Free tools: password generator, currency converter, word counter. No login required." />
          </>
        )}
      </Helmet>

      <div className="min-h-screen bg-[#FFF8F0]">
        <PublicHeader
          leftContent={
            selectedTool ? (
              <button
                onClick={() => navigate('/tools')}
                className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors"
              >
                <MdArrowBack className="w-5 h-5" />
                <span className="hidden sm:inline">All Tools</span>
              </button>
            ) : undefined
          }
        />

        <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {selectedTool && ToolComponent ? (
            <article className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6 sm:p-8">
              <header className="mb-6 flex items-start gap-3">
                <div className={`p-2 rounded-xl shrink-0 ${(COLOR_CLASSES[selectedTool.color] || COLOR_CLASSES.orange).bg}`}>
                  {(() => {
                    const Icon = selectedTool.icon;
                    return <Icon className={`text-2xl ${(COLOR_CLASSES[selectedTool.color] || COLOR_CLASSES.orange).text}`} />;
                  })()}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{progSEO?.h1 ?? seo?.h1 ?? selectedTool.name}</h1>
                  <p className="text-gray-600 text-sm mt-1">{progSEO ? 'Convert instantly with our free online tool. No signup required.' : (seo?.subtitle ?? selectedTool.description)}</p>
                </div>
              </header>

              <section id="tool" className="mb-8 pt-6 border-t border-gray-200" aria-labelledby="use-tool">
                <h2 id="use-tool" className="text-lg font-semibold text-gray-800 mb-4">
                  {progSEO ? 'Convert Now' : (seo?.toolSectionTitle ?? `Use the ${selectedTool.name}`)}
                </h2>
                <ToolComponent slug={slug} />
              </section>

              {progSEO ? (
                <>
                  {progSEO.h2Sections.map((section, i) => (
                    <section key={i} className="mb-8 pt-6 border-t border-gray-200" aria-labelledby={`section-${i}`}>
                      <h2 id={`section-${i}`} className="text-lg font-semibold text-gray-800 mb-3">{section.heading}</h2>
                      <p className="text-gray-600 text-sm leading-relaxed">{section.content}</p>
                    </section>
                  ))}
                </>
              ) : seo ? (
                <>
                  <section className="mb-8 pt-6 border-t border-gray-200" aria-labelledby="what-is">
                    <h2 id="what-is" className="text-lg font-semibold text-gray-800 mb-3">What is this tool</h2>
                    <p className="text-gray-600 text-sm leading-relaxed">{seo.whatIs}</p>
                  </section>

                  {seo.howToSteps?.length ? (
                    <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="how-to-use">
                      <h2 id="how-to-use" className="text-lg font-semibold text-gray-800 mb-3">How to use this tool</h2>
                      <ol className="list-decimal list-inside space-y-2 text-gray-600 text-sm">
                        {seo.howToSteps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    </section>
                  ) : null}

                  {seo.features?.length ? (
                    <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="key-features">
                      <h2 id="key-features" className="text-lg font-semibold text-gray-800 mb-3">Key features</h2>
                      <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                        {seo.features.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </section>
                  ) : null}

                  {seo.benefits ? (
                    <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="benefits">
                      <h2 id="benefits" className="text-lg font-semibold text-gray-800 mb-3">Benefits of using this tool</h2>
                      <p className="text-gray-600 text-sm leading-relaxed">{seo.benefits}</p>
                    </section>
                  ) : null}
                </>
              ) : null}

              {(progSEO?.faqs?.length || seo?.faqs?.length) ? (
                <section
                  className="mt-8 pt-6 border-t border-gray-200"
                  aria-labelledby="faq"
                  itemScope
                  itemType="https://schema.org/FAQPage"
                >
                  <h2 id="faq" className="text-lg font-semibold text-gray-800 mb-4">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {(progSEO?.faqs ?? seo?.faqs)!.map((faq, i) => (
                      <div
                        key={i}
                        itemScope
                        itemProp="mainEntity"
                        itemType="https://schema.org/Question"
                        className="bg-orange-50/30 rounded-xl p-4 border border-orange-100/60"
                      >
                        <h3 className="font-medium text-gray-800 text-sm mb-2" itemProp="name">{faq.question}</h3>
                        <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                          <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">{faq.answer}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="conversion mt-8 pt-6 border-t border-gray-200" aria-labelledby="stay-focused">
                <h2 id="stay-focused" className="text-lg font-semibold text-gray-800 mb-3">⚡ Stay Focused While Working</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Managing invoices, finances, or writing tasks requires concentration.
                  Use Focuzon Deep Work to finish work faster without distractions.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/dashboard"
                    className="cta inline-block px-5 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors"
                  >
                    Start Focus Session
                  </Link>
                </div>
              </section>

              {progSEO?.relatedLinks?.length ? (
                <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="related-conversions">
                  <h2 id="related-conversions" className="text-lg font-semibold text-gray-800 mb-3">Related Conversions</h2>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    {progSEO.relatedLinks.map((link) => (
                      <li key={link.path}>
                        <Link to={link.path} className="text-orange-600 hover:text-orange-700 hover:underline">{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="productivity">
                <h2 id="productivity" className="text-lg font-semibold text-gray-800 mb-3">Productivity Techniques Used in Focuzon</h2>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li><Link to="/pomodoro-timer" className="text-orange-600 hover:text-orange-700 hover:underline">Pomodoro Timer</Link></li>
                  <li><Link to="/deep-work" className="text-orange-600 hover:text-orange-700 hover:underline">Deep Work</Link></li>
                  <li><Link to="/focus-sounds" className="text-orange-600 hover:text-orange-700 hover:underline">Focus Sounds</Link></li>
                  <li><Link to="/task-manager" className="text-orange-600 hover:text-orange-700 hover:underline">Task Manager</Link></li>
                </ul>
              </section>

              <section className="mt-8 pt-6 border-t border-gray-200" aria-labelledby="other-tools">
                <h2 id="other-tools" className="text-lg font-semibold text-gray-800 mb-3">Other Free Tools</h2>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  {FREE_TOOLS.filter((t) => t.id !== selectedTool.id && (!t.requiresLogin || isAuthenticated)).map((tool) => (
                    <li key={tool.id}>
                      <Link to={tool.path} className="text-orange-600 hover:text-orange-700 hover:underline">
                        {tool.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

            </article>
          ) : (
            <>
              <div className="text-center mb-12">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                  Free Productivity Tools
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Choose a tool below. All tools are free to use, no login required.
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full text-green-700 text-sm font-medium">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Free forever · No signup needed
                </div>
              </div>

              {/* CTA: Start Focus Session */}
              <section className="mb-10 p-6 bg-orange-50 border border-orange-100 rounded-2xl text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Ready to focus?</h2>
                <p className="text-gray-600 text-sm mb-4 max-w-xl mx-auto">
                  Use these tools, then start a structured focus session with Focuzon.
                </p>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-700 transition-colors"
                >
                  Start Focus Session
                </Link>
              </section>

              <div className="space-y-10">
                {CATEGORY_ORDER.map((cat) => {
                  const toolsInCat = visibleTools.filter((t) => t.category === cat);
                  if (toolsInCat.length === 0) return null;
                  return (
                    <div key={cat}>
                      <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-orange-100">
                        {CATEGORY_LABELS[cat]}
                      </h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {toolsInCat.map((tool) => {
                          const Icon = tool.icon;
                          const colorClasses = COLOR_CLASSES[tool.color] || COLOR_CLASSES.orange;
                          return (
                            <button
                              key={tool.id}
                              onClick={() => navigate(`/tools/${tool.id}`)}
                              className="flex flex-col items-center p-6 bg-white rounded-xl border-2 border-orange-100 hover:border-orange-300 hover:shadow-md transition-all duration-200 text-left group"
                            >
                              <div className={`p-3 rounded-xl ${colorClasses.bg} mb-3 group-hover:scale-110 transition-transform`}>
                                <Icon className={`text-2xl ${colorClasses.text}`} />
                              </div>
                              <span className="font-semibold text-gray-900 text-sm text-center">{tool.name}</span>
                              <span className="text-xs text-gray-500 mt-1 text-center line-clamp-2">{tool.description}</span>
                              {tool.requiresLogin && (
                                <span className="mt-1 text-[10px] text-blue-600 font-medium">Login to sync</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default FreeToolsPage;
